import { SerializableException } from '@core/services/errors';
import {
  setErrorReporter,
  setBreadcrumbReporter,
  setTagReporter,
  setSpanReporter,
  type SpanOptions,
} from '@core/lib/errorReporter';

type SentryLike = {
  withScope: (
    callback: (scope: { setExtra: (key: string, value: unknown) => void }) => void
  ) => void;
  captureException: (error: unknown) => void;
  addBreadcrumb: (breadcrumb: { message: string; data?: Record<string, unknown> }) => void;
  setTag: (key: string, value: string) => void;
  startSpan: <T>(options: SpanOptions, callback: () => T) => T;
};

/**
 * Shared beforeSend filter that drops SerializableException instances.
 * These are expected application errors (404, 409, validation) that should not be sent to Sentry.
 * Uses generic types to satisfy each Sentry SDK's specific event type (ErrorEvent, Event, etc.).
 */
export const beforeSendFilter = <E, H extends { originalException?: unknown }>(
  event: E,
  hint: H
): E | null => {
  const exc = hint.originalException;
  if (exc instanceof SerializableException) return null;
  if (exc != null && typeof exc === 'object' && 'isUpstream' in exc && exc.isUpstream) return null;
  return event;
};

/**
 * Wire up the error reporter abstraction to a Sentry-compatible SDK.
 * Used by sentry.server.config, sentry.edge.config, proxy instrument, and CLI instrument.
 */
export const setupSentryReporters = (Sentry: SentryLike) => {
  setErrorReporter((error, context) => {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  });

  setBreadcrumbReporter((message, data) => {
    Sentry.addBreadcrumb({ message, data });
  });

  setTagReporter((key, value) => {
    Sentry.setTag(key, value);
  });

  setSpanReporter((options, fn) => Sentry.startSpan(options, fn));
};
